import type {
  WebshopStaticContent,
  WebshopStaticContentContentTypeLiteral,
} from '@cldsystems/ws-api-client';
import { CoreConfig } from '@/configs/core/core.config';
import { FooterStaticContent } from '@/configs/footer/footer.config';
import { HeaderStaticContent } from '@/configs/header/header.config';
import { LandingStaticContent } from '@/configs/landing/landing.config';
import { ProductDetailStaticContent } from '@/configs/product-general-info/product-general-info.config';
import { GET_LIST_MAX_SIZE } from '@/constants/constants';
import { List } from '@/types/common';
import { Filter } from '@/types/filter';
import { logAxiosError } from '@/utils/axios-error-logger';
import { resolveStaticContentPayload } from '@/utils/static-content';
import { httpClient } from './services.setup';

const FROM = 0;
const SIZE = GET_LIST_MAX_SIZE;

export const getStaticContent = async <T extends WebshopStaticContentContentTypeLiteral>(
  contentType: T,
): Promise<
  T extends 'FOOTER'
    ? FooterStaticContent
    : T extends 'HEADER'
      ? HeaderStaticContent
      : T extends 'LANDING_PAGE'
        ? LandingStaticContent
        : T extends 'PRODUCT_DETAIL'
          ? ProductDetailStaticContent
          : T extends 'CORE'
            ? CoreConfig
            : T extends 'PRODUCT_CARD'
              ? string
              : never
> => {
  const projectId = process.env.NEXT_PUBLIC_PROJECT_ID;
  const filters: Filter<WebshopStaticContent>[] = [
    {
      key: 'stage',
      operator: 'not_equals',
      value: 'Inactive',
    },
    {
      key: 'contentType',
      operator: 'equals',
      value: contentType,
    },
    ...(projectId
      ? [
          {
            key: 'projectId' as keyof WebshopStaticContent,
            operator: 'equals' as const,
            value: projectId,
          },
        ]
      : []),
  ];
  const filter = encodeURIComponent(
    filters.map(f => `${f.key}[${f.operator}]=${f.value}`).join(','),
  );
  try {
    const { data: staticContent } = await httpClient.get<List<WebshopStaticContent>>(
      `/v1/webshop/static-content/list/${FROM}/${SIZE}?filter=${filter}`,
    );
    if (!staticContent.items?.length) {
      throw new Error(`No ${contentType} content found`);
    }
    const payload = resolveStaticContentPayload(staticContent.items[0].content);
    return payload as T extends 'FOOTER'
      ? FooterStaticContent
      : T extends 'HEADER'
        ? HeaderStaticContent
        : T extends 'LANDING_PAGE'
          ? LandingStaticContent
          : T extends 'PRODUCT_DETAIL'
            ? ProductDetailStaticContent
            : T extends 'CORE'
              ? CoreConfig
              : T extends 'PRODUCT_CARD'
                ? string
                : never;
  } catch (error) {
    logAxiosError(error);
    const message = error instanceof Error ? error.message : `Failed to fetch ${contentType}`;
    throw new Error(message);
  }
};
